import { setHighlightedEntities, setInvisibleEntities, setSelectedEntities } from "@/Context/ViewerContext/Reducer";
import { ViewerContext } from "@/Context/ViewerContext/ViewerContextProvider";
import { XktContext } from "@/Context/XktContext/XktContextProvider";
import useFlyTo from "@/Hooks/FlyTo/FlyTo";
import { useIsMobile } from "@/Hooks/useIsMobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import clsx from "clsx";
import { ChevronRight, CircleDot, Eye, EyeOff, Grip, MoreHorizontal, Plane, Search } from "lucide-react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Marker } from "react-mark.js";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Separator } from "../ui/separator";

export type TreeRenderElement = {
    id: string;
    name: string;
    type: string;
    parent?: string;
    children: TreeRenderElement[];
    depth: number;
    isExpanded: boolean;
};

interface TreeMenuProps {
    data: TreeRenderElement;
    selectedToggle: (value: TreeRenderElement) => void;
    isolateToggle: (value: TreeRenderElement) => void;
    flyTo: () => void;
}

export interface TreeviewRendererProps {
    data: TreeRenderElement[];
    setData: (data: TreeRenderElement[]) => void;
}

const TreeMenu = ({ data, selectedToggle, isolateToggle, flyTo }: TreeMenuProps) => {
    const { t } = useTranslation();
    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">{t("treeViewOpenMenu")}</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[160px]">
                    <DropdownMenuItem onClick={() => selectedToggle(data)}>
                        <Grip /> {t("treeViewSelect")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={flyTo}>
                        <Plane /> {t("treeViewFlyTo")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => isolateToggle(data)}>
                        <CircleDot /> {t("treeViewIsolate")}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
};

function RenderElementName(
    handleElementClick: () => void,
    hoverNode: (element: TreeRenderElement) => void,
    element: TreeRenderElement,
    clearNode: () => void,
    highlight: string | undefined
) {
    const isMobile = useIsMobile();

    return (
        <div
            onClick={handleElementClick}
            className={clsx([
                "inline-block flex-1 items-center rounded-md hover:bg-accent cursor-pointer min-w-0 truncate",
                isMobile ? "pr-1 mr-1" : "px-1 mx-1",
            ])}
            onMouseEnter={() => {
                hoverNode(element);
            }}
            onMouseLeave={() => {
                clearNode();
            }}
        >
            <Marker as={"small"} mark={highlight} options={{ separateWordSearch: false }}>
                {element.name ?? element.type}
            </Marker>
        </div>
    );
}

export default function Component({ data, setData }: TreeviewRendererProps) {
    const [flattenedData, setFlattenedData] = useState<TreeRenderElement[]>([]);
    const listRef = useRef<List>(null);
    const [query, setQuery] = useState("");
    const [selectedObject, setSelectedObject] = useState(-1);
    const { state: viewerState } = useContext(ViewerContext);
    const [opened, setOpened] = useState(false);
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const flattenTree = (tree: TreeRenderElement[], filter: string, changed: boolean): TreeRenderElement[] => {
        const result: TreeRenderElement[] = [];
        let newSelectedObject = -1;

        function matchesFilter(node: TreeRenderElement, filter: string): boolean {
            if (!filter) return true;

            if (node.name && node.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())) {
                return true;
            }

            if (node.children) {
                return node.children.some((child) => matchesFilter(child, filter));
            }

            return false;
        }

        function traverse(node: TreeRenderElement) {
            const nodeMatches = !filter || matchesFilter(node, filter);

            if (nodeMatches) {
                if (changed && filter) {
                    node.isExpanded = true;
                }

                result.push(node);

                if (viewerState.selectedEntities.length === 1 && node.id === viewerState.selectedEntities[0]) {
                    newSelectedObject = result.length - 1;
                }

                if (node.children && node.isExpanded) {
                    node.children.forEach(traverse);
                }
            }
        }

        tree.forEach(traverse);

        if (newSelectedObject !== -1) {
            setSelectedObject(newSelectedObject);
        }

        return result;
    };
    const queryOld = useRef<string>("");
    useEffect(() => {
        if (!data) return;
        setFlattenedData(flattenTree(data, query, queryOld.current != query));
        queryOld.current = query;
    }, [data, query]);

    useEffect(() => {
        setOpened(true);
    }, []);

    const toggleNode = useCallback(
        (id: string) => {
            const element = flattenedData.find((x) => x.id === id);
            if (!element) return;
            element.isExpanded = !element.isExpanded;
            setData([...data]);
        },
        [data, flattenedData]
    );

    const Row = useCallback(
        ({ index, style }: { index: number; style: React.CSSProperties }) => {
            const element = flattenedData[index];
            const parent = flattenedData.find((x) => x.id === element.parent);
            let isLast = true;

            if (parent && parent.children) {
                const childIndex = parent.children.findIndex((x) => x.id === element.id);

                isLast = childIndex + 1 === parent.children.length;
            }

            return (
                <TreeNode
                    element={flattenedData[index]}
                    style={style}
                    highlight={query}
                    isLastChild={isLast}
                    data={data}
                    setData={setData}
                    toggleNode={toggleNode}
                />
            );
        },
        [flattenedData, toggleNode]
    );

    const lastSelectedEntities = useRef<string>("");
    const TreeNode = ({
        element,
        style,
        highlight,
        isLastChild,
        toggleNode,
        data,
        setData,
    }: {
        element: TreeRenderElement;
        style: React.CSSProperties;
        highlight: string | undefined;
        isLastChild: boolean;
        toggleNode: (id: string) => void;
        data: TreeRenderElement[];
        setData: (data: TreeRenderElement[]) => void;
    }) => {
        const isMobile = useIsMobile();

        const { flyTo } = useFlyTo();
        const { state: viewerState, dispatch } = useContext(ViewerContext);
        const { state } = useContext(XktContext);
        const { t } = useTranslation();
        const handleElementClick = () => {
            element.isExpanded = selectedSet(element);
            setData([...data]);
        };

        const selectedSet = (value: TreeRenderElement) => {
            const contains = viewerState.selectedEntities.some((x) => (x as string).includes(value.id));
            const isParent = value.children ? value.children.length != 0 : false;
            if (!isParent && contains && viewerState.selectedEntities.length == 1) {
                dispatch(setSelectedEntities([]));
                return false;
            }
            if (!isParent || !contains) {
                const flattenObjects = [value.id];
                const recChildIdGetter = (obj: TreeRenderElement) => {
                    flattenObjects.push(obj.id);
                    if (obj.children) {
                        for (const child of obj.children) {
                            recChildIdGetter(child);
                        }
                    }
                };
                recChildIdGetter(value);
                dispatch(setSelectedEntities(flattenObjects));
                return true;
            } else {
                dispatch(setSelectedEntities([]));
            }
            return false;
        };

        const hoverNode = (element: TreeRenderElement) => {
            const selectedIds = [element.id];
            const recursiveSelect = (datas: TreeRenderElement[]) => {
                for (const data of datas) {
                    selectedIds.push(data.id);
                    if (data.children) {
                        recursiveSelect(data.children);
                    }
                }
            };
            if (element.children) {
                recursiveSelect(element.children);
            }
            //if (selectedIds.length < 750)
            dispatch(setHighlightedEntities(selectedIds));
        };
        const clearNode = () => {
            dispatch(setHighlightedEntities([]));
        };

        const isDataVisible = (element: TreeRenderElement): boolean => {
            if (viewerState.invisibleEntitiesHash.has(element.id)) {
                return false;
            }

            if (element.children && element.children.length > 0) {
                for (const child of element.children) {
                    if (isDataVisible(child)) {
                        return true;
                    }
                }
                return false;
            }

            return true;
        };
        const getRawElementRecursive = (
            element: TreeRenderElement,
            list: TreeRenderElement[]
        ): TreeRenderElement | undefined => {
            for (const e of list) {
                if (e.id == element.id) {
                    return e;
                }
                if (e.children) {
                    const child = getRawElementRecursive(element, e.children);
                    if (child) {
                        return child;
                    }
                }
            }
            return undefined;
        };

        const setVisible = (treeData: TreeRenderElement) => {
            const element = getRawElementRecursive(treeData, data) as TreeRenderElement;
            if (!element) return;
            const visibles = [element.id];
            const recChildIdGetter = (obj: TreeRenderElement) => {
                visibles.push(obj.id);
                if (obj.children) {
                    for (const child of obj.children) {
                        recChildIdGetter(child);
                    }
                }
            };
            recChildIdGetter(element);
            const visiblesSet = new Set(visibles);
            const filteredInvisibleEntities = viewerState.invisibleEntities.filter(
                (x) => !visiblesSet.has(x as string)
            );
            dispatch(setInvisibleEntities(filteredInvisibleEntities));
        };
        const setInvisible = (treeData: TreeRenderElement) => {
            const element = getRawElementRecursive(treeData, data) as TreeRenderElement;
            if (!element) return;
            const invisibles = [element.id];
            const recChildIdGetter = (obj: TreeRenderElement) => {
                invisibles.push(obj.id);
                if (obj.children) {
                    for (const child of obj.children) {
                        recChildIdGetter(child);
                    }
                }
            };
            recChildIdGetter(element);
            dispatch(setInvisibleEntities([...viewerState.invisibleEntities, ...invisibles]));
        };
        const selectedToggle = (value: TreeRenderElement) => {
            const flattenObjects = [value.id];
            const recChildIdGetter = (obj: TreeRenderElement) => {
                flattenObjects.push(obj.id);
                if (obj.children) {
                    for (const child of obj.children) {
                        recChildIdGetter(child);
                    }
                }
            };
            recChildIdGetter(value);
            const selectedEntitiesSet = new Set(viewerState.selectedEntities);

            const existingObjects = flattenObjects.filter((x) => selectedEntitiesSet.has(x));

            const existingObjectsSet = new Set(existingObjects);

            const fo = [
                ...viewerState.selectedEntities.filter((x) => !existingObjectsSet.has(x)),
                ...flattenObjects.filter((x) => !existingObjectsSet.has(x)),
            ];
            dispatch(setSelectedEntities(fo));
        };
        const isolateToggle = (value: TreeRenderElement) => {
            if (!state.viewer) return;
            const flattenObjects = [value.id];
            const recChildIdGetter = (obj: TreeRenderElement) => {
                flattenObjects.push(obj.id);
                if (obj.children) {
                    for (const child of obj.children) {
                        recChildIdGetter(child);
                    }
                }
            };
            recChildIdGetter(value);

            const objectsToHide = Object.keys(state.viewer.scene.objects).filter((id) => !flattenObjects.includes(id));

            dispatch(setInvisibleEntities(objectsToHide));
        };

        const flyLogic = () => {
            if (!element.children || element.children.length === 0) {
                flyTo([element.id]);
            } else {
                const flattenObjects = (children: TreeRenderElement[]) => {
                    const objs: TreeRenderElement[] = [];
                    for (const child of children) {
                        if (child.children && child.children.length > 0) {
                            objs.push(...flattenObjects(child.children));
                        } else {
                            objs.push(child);
                        }
                    }
                    return objs;
                };
                const objects = flattenObjects(element.children);
                const ids = objects.map((x) => x.id);
                flyTo(ids);
            }
        };

        const stepOpen = (nodes: TreeRenderElement[]): boolean => {
            for (const node of nodes) {
                if (viewerState.selectedEntities.includes(node.id)) {
                    node.isExpanded = true;
                    return true;
                }
                if (node.children) {
                    if (stepOpen(node.children)) {
                        node.isExpanded = true;
                        return true;
                    }
                }
            }

            return false;
        };

        useEffect(() => {
            if (viewerState.selectedEntities.length === 1) {
                if (lastSelectedEntities.current != viewerState.selectedEntities[0]) {
                    lastSelectedEntities.current = viewerState.selectedEntities[0];
                    stepOpen(data);
                    setData([...data]);
                }
            }
        }, [viewerState.selectedEntities]);

        const isLeaf = !element.children || element.children.length === 0;
        let [modelName, elementName] = ["", ""];
        if (element.id && element.id.includes("#")) [modelName, elementName] = element.id.split("#");

        const getSelectedColor = (data: TreeRenderElement): string => {
            if (viewerState.selectedEntities.includes(data.id)) {
                return "rgba(180,180,180,0.1)";
            }
            return "transparent";
        };

        return (
            <div
                className={clsx(["flex items-center", !isMobile && "px-1"])}
                style={{ ...style, background: getSelectedColor(element) }}
            >
                {/* <div style={{ marginLeft: `${element.depth * 12}px` }}></div> */}
                <span className="inline-block h-full">
                    {Array.from({ length: element.depth }).map((_, index) => (
                        <span key={index} className={`tree-indent-unit`} />
                    ))}
                </span>

                <span className={`tree-switcher`}>
                    {isLeaf ? (
                        isLastChild ? (
                            <span className="tree-switcher-leaf-line tree-switcher-leaf-line-last" />
                        ) : (
                            <span className="tree-switcher-leaf-line" />
                        )
                    ) : (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="w-6 h-6 p-2"
                            onClick={() => toggleNode(element.id)}
                        >
                            <ChevronRight
                                className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                                    element.isExpanded ? "rotate-90" : ""
                                }`}
                            />
                        </Button>
                    )}
                </span>
                {!element.name.includes("Anonymous Type") && elementName != modelName ? (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            {RenderElementName(handleElementClick, hoverNode, element, clearNode, highlight)}
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>
                                {element.name}
                                {element.type && element.name != element.type && ` (${element.type})`}
                            </p>
                            <p>{element.id != element.name && element.id.replace("#", " ")}</p>
                        </TooltipContent>
                    </Tooltip>
                ) : (
                    RenderElementName(handleElementClick, hoverNode, element, clearNode, highlight)
                )}
                {isDataVisible(element) ? (
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setInvisible(element)}>
                        <Eye className="h-4 w-4" />
                        <span className="sr-only">{t("treeViewHideItem")}</span>
                    </Button>
                ) : (
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setVisible(element)}>
                        <EyeOff className="h-4 w-4" />
                        <span className="sr-only">{t("treeViewShowItem")}</span>
                    </Button>
                )}

                <TreeMenu
                    data={element}
                    selectedToggle={selectedToggle}
                    isolateToggle={isolateToggle}
                    flyTo={() => flyLogic()}
                />
            </div>
        );
    };

    useEffect(() => {
        if (selectedObject != -1) {
            listRef.current?.scrollToItem(selectedObject);
            setSelectedObject(-1);
        }
    }, [selectedObject]);

    return !opened ? null : (
        <>
            <div className="items-center mx-4 mb-2">
                <small>
                    <Input
                        startIcon={Search}
                        type="text"
                        id="filter"
                        placeholder={t("treeViewSearchPlaceholder")}
                        value={query}
                        onChange={(x) => setQuery(x.currentTarget.value)}
                        onClear={() => {
                            setQuery("");
                        }}
                    />
                </small>
            </div>
            <Separator className="mt-2" />
            {isMobile ? (
                <div style={{ height: "calc(60vh - 8px)" }}>
                    <AutoSizer disableWidth className="">
                        {({ height }) => (
                            <List
                                ref={listRef}
                                height={height - 174}
                                itemCount={flattenedData.length}
                                itemSize={30}
                                width={"100%"}
                            >
                                {Row}
                            </List>
                        )}
                    </AutoSizer>
                </div>
            ) : (
                <AutoSizer disableWidth className="">
                    {({ height }) => (
                        <List
                            ref={listRef}
                            height={height - 174}
                            itemCount={flattenedData.length}
                            itemSize={30}
                            width={"100%"}
                        >
                            {Row}
                        </List>
                    )}
                </AutoSizer>
            )}
        </>
    );
}
